import { Alignment } from 'root/consts';
import { getAlignmentText } from 'root/utils/alignment';
import {
  DEFAULT_BACKGROUND_HORIZONTAL_PADDING,
  DEFAULT_BACKGROUND_VERTICAL_PADDING,
  DEFAULT_ITEM_IMAGE_RATIO,
  DEFAULT_ITEM_IMAGE_SIDE_PADDING,
  DEFAULT_ITEM_IMAGE_VERTICAL_PADDING,
  DEFAULT_ITEM_INFO_SIDE_PADDING,
  DEFAULT_ITEM_VERTICAL_PADDING,
  DEFAULT_ITEMS_COLUMN_GAP,
  DEFAULT_ITEMS_ROW_GAP,
  DEFAULT_MAX_ITEMS_PER_ROW,
  DEFAULT_SLIDER_ARROW_DISTANCE,
  ITEM_IMAGE_PADDING_MAX,
  ITEM_INFO_SIDE_PADDING_MAX,
  MAX_ITEMS_PER_ROW_MAX,
  MAX_ITEMS_PER_ROW_MIN,
  MENU_ADD_ON_SPACING_MAX,
  MENU_ADD_ON_SPACING_MIN,
  MenuAddOnImageResizeMode,
  MenuAddOnItemsDisplayMode,
  MenuAddOnSliderArrowPosition,
  SLIDER_ARROW_DISTANCE_MAX,
  SLIDER_ARROW_DISTANCE_MIN,
  clampStyleNumber,
} from 'root/components/MenuAddOn/Settings/Tabs/LayoutTab/consts';

// Fixed width of `.sliderButton` in Widget.st.css; Outside arrows reserve it next to the configured distance.
const SLIDER_BUTTON_SIZE = 40;

export const getJustifyContent = (alignment: Alignment) => {
  switch (alignment) {
    case Alignment.Center:
      return 'center';
    case Alignment.End:
      return 'flex-end';
    case Alignment.Start:
    default:
      return 'flex-start';
  }
};

const getImageObjectFit = (resizeMode: MenuAddOnImageResizeMode) =>
  resizeMode === MenuAddOnImageResizeMode.Fit ? 'contain' : 'cover';

const px = (value: number) => `${value}px`;

// Unset params keep their registered default (see `stylesParams.ts`) instead of falling back to Start.
const toAlignment = (value: unknown, fallback: Alignment): Alignment =>
  typeof value === 'number' && value in Alignment ? value : fallback;

/**
 * Layout-tab values the widget needs as CSS. Returned from `customCssVars` (see `stylesParams.ts`) so the platform
 * recomputes them on every style change: Classic Editor applies style-only updates as CSS vars without re-rendering
 * the React widget, so anything derived in JS from these params would stay stale until a reload.
 */
export const getMenuAddOnLayoutCssVars = ({
  isMobile,
  numbers,
}: {
  isMobile: boolean;
  numbers: Record<string, unknown>;
}): Record<string, string | number> => {
  const maxItemsPerRow = clampStyleNumber(
    numbers.menuAddOnMaxItemsPerRow,
    DEFAULT_MAX_ITEMS_PER_ROW,
    MAX_ITEMS_PER_ROW_MIN,
    MAX_ITEMS_PER_ROW_MAX
  );
  const columnGap = clampStyleNumber(
    numbers.menuAddOnItemsColumnGap,
    DEFAULT_ITEMS_COLUMN_GAP,
    MENU_ADD_ON_SPACING_MIN,
    MENU_ADD_ON_SPACING_MAX
  );
  const rowGap = clampStyleNumber(
    numbers.menuAddOnItemsRowGap,
    DEFAULT_ITEMS_ROW_GAP,
    MENU_ADD_ON_SPACING_MIN,
    MENU_ADD_ON_SPACING_MAX
  );
  const sliderArrowDistance = clampStyleNumber(
    numbers.menuAddOnSliderArrowDistance,
    DEFAULT_SLIDER_ARROW_DISTANCE,
    SLIDER_ARROW_DISTANCE_MIN,
    SLIDER_ARROW_DISTANCE_MAX
  );
  const isSlider = numbers.menuAddOnItemsDisplayMode === MenuAddOnItemsDisplayMode.Slider;
  const areSliderArrowsOutside =
    isSlider && numbers.menuAddOnSliderArrowPosition === MenuAddOnSliderArrowPosition.Outside;
  const itemInfoAlignment = toAlignment(numbers.menuAddOnItemInfoAlignment, Alignment.Start);
  const displayedMaxItemsPerRow = isMobile ? Math.min(maxItemsPerRow, 2) : maxItemsPerRow;
  const itemWidth = `calc((100% - ${columnGap * (displayedMaxItemsPerRow - 1)}px) / ${displayedMaxItemsPerRow})`;

  return {
    // Shared with Menus/Item + Menus/Items styles; override the Menus `customCssVars` values for this widget.
    itemsColumnGap: px(columnGap),
    itemsRowGap: px(rowGap),
    itemImageAspectRatio: (numbers.menuAddOnItemImageRatio as number) || DEFAULT_ITEM_IMAGE_RATIO,
    itemTextAlignment: getAlignmentText(itemInfoAlignment),
    nameAndPriceJustifyContent: getJustifyContent(itemInfoAlignment),
    menuAddOnAddToCartButtonJustifyContent: getJustifyContent(
      toAlignment(numbers.menuAddOnAddToCartButtonAlignment, Alignment.Center)
    ),

    menuAddOnLayoutRootPaddingBlock: px(
      clampStyleNumber(
        numbers.menuAddOnBackgroundVerticalPadding,
        DEFAULT_BACKGROUND_VERTICAL_PADDING,
        MENU_ADD_ON_SPACING_MIN,
        MENU_ADD_ON_SPACING_MAX
      )
    ),
    menuAddOnLayoutRootPaddingInline: px(
      clampStyleNumber(
        numbers.menuAddOnBackgroundHorizontalPadding,
        DEFAULT_BACKGROUND_HORIZONTAL_PADDING,
        MENU_ADD_ON_SPACING_MIN,
        MENU_ADD_ON_SPACING_MAX
      )
    ),
    menuAddOnLayoutHeaderTextAlign: getAlignmentText(toAlignment(numbers.menuAddOnHeaderAlignment, Alignment.Center)),
    // Slider rows always start at the leading edge so the scroll track isn't shifted by card alignment.
    menuAddOnLayoutCardsJustifyContent: isSlider
      ? 'flex-start'
      : getJustifyContent(toAlignment(numbers.menuAddOnCardAlignment, Alignment.Start)),
    menuAddOnLayoutItemWidth: itemWidth,
    menuAddOnLayoutItemInfoSidePadding: px(
      clampStyleNumber(
        numbers.menuAddOnItemInfoSidePadding,
        DEFAULT_ITEM_INFO_SIDE_PADDING,
        MENU_ADD_ON_SPACING_MIN,
        ITEM_INFO_SIDE_PADDING_MAX
      )
    ),
    menuAddOnLayoutItemVerticalPadding: px(
      clampStyleNumber(
        numbers.menuAddOnItemVerticalPadding,
        DEFAULT_ITEM_VERTICAL_PADDING,
        MENU_ADD_ON_SPACING_MIN,
        ITEM_INFO_SIDE_PADDING_MAX
      )
    ),
    menuAddOnLayoutItemImageObjectFit: getImageObjectFit(
      numbers.menuAddOnItemImageResizeMode as MenuAddOnImageResizeMode
    ),
    menuAddOnLayoutItemImageSidePadding: px(
      clampStyleNumber(
        numbers.menuAddOnItemImageSidePadding,
        DEFAULT_ITEM_IMAGE_SIDE_PADDING,
        MENU_ADD_ON_SPACING_MIN,
        ITEM_IMAGE_PADDING_MAX
      )
    ),
    menuAddOnLayoutItemImageVerticalPadding: px(
      clampStyleNumber(
        numbers.menuAddOnItemImageVerticalPadding,
        DEFAULT_ITEM_IMAGE_VERTICAL_PADDING,
        MENU_ADD_ON_SPACING_MIN,
        ITEM_IMAGE_PADDING_MAX
      )
    ),
    menuAddOnLayoutLoadMoreJustifyContent: getJustifyContent(
      toAlignment(numbers.menuAddOnLoadMoreButtonAlignment, Alignment.Center)
    ),
    menuAddOnLayoutViewFullMenuJustifyContent: getJustifyContent(
      toAlignment(numbers.menuAddOnViewFullMenuButtonAlignment, Alignment.Center)
    ),

    // Grid vs Slider. One DOM structure for both; these toggle the slider-only behavior.
    menuAddOnLayoutItemsFlexWrap: isSlider ? 'nowrap' : 'wrap',
    menuAddOnLayoutItemsOverflowX: isSlider ? 'auto' : 'visible',
    menuAddOnLayoutItemsOverflowY: isSlider ? 'hidden' : 'visible',
    // Menus/Items gives the list a 20px top margin; the slider track sits flush.
    menuAddOnLayoutItemsMarginTop: isSlider ? '0px' : '20px',
    menuAddOnLayoutItemsScrollSnapType: isSlider ? 'x mandatory' : 'none',
    menuAddOnLayoutItemMinWidth: isSlider ? itemWidth : '0',
    menuAddOnLayoutViewportOverflow: isSlider ? 'hidden' : 'visible',
    menuAddOnLayoutSliderButtonDisplay: isSlider ? 'flex' : 'none',
    menuAddOnLayoutLoadMoreDisplay: isSlider ? 'none' : 'flex',
    menuAddOnLayoutSliderTrackPaddingInline: areSliderArrowsOutside
      ? `calc(${px(sliderArrowDistance)} + ${px(SLIDER_BUTTON_SIZE)})`
      : '0px',
    menuAddOnLayoutSliderButtonOffset: areSliderArrowsOutside ? '0px' : px(sliderArrowDistance),
  };
};
